import React, { useState } from 'react';
import ExportEngine from '../../utils/export/exportEngine';
import useActiveCompanyProfile from '../../hooks/useActiveCompanyProfile';
import { useCompanyCurrency } from '../../hooks/useCompanyCurrency';
import { toast } from 'react-hot-toast';

const GlobalExportButton = ({
  data,
  config = {},
  className = '',
  disabled = false,
  onExportStart,
  onExportComplete,
  onExportError
}) => {
  const { companyProfile } = useActiveCompanyProfile();
  const { currency } = useCompanyCurrency();
  const [isExporting, setIsExporting] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  const exportOptions = [
    { key: 'pdf', label: 'PDF', icon: '📄', description: 'Document format' },
    { key: 'excel', label: 'Excel', icon: '📊', description: 'Spreadsheet format' },
    { key: 'csv', label: 'CSV', icon: '📈', description: 'Comma-separated values' },
    { key: 'json', label: 'JSON', icon: '⚙️', description: 'Data format' },
    { key: 'image', label: 'Image', icon: '🖼️', description: 'Screenshot format' }
  ];

  const handleExport = async (format) => {
    if (!data || (Array.isArray(data) && data.length === 0)) {
      toast.error('No data available for export');
      return;
    }

    setIsExporting(true);
    setShowOptions(false);

    try {
      if (onExportStart) onExportStart(format);

      const exportEngine = new ExportEngine({
        company: companyProfile,
        currency: currency
      });

      let result;

      switch (format) {
        case 'pdf':
          result = await exportEngine.exportToPDF(data, {
            ...config,
            title: config.title || 'ACCTOO Export',
            filename: config.filename || `${config.title || 'export'}-pdf`
          });
          break;

        case 'excel':
          result = await exportEngine.exportToExcel(data, {
            ...config,
            title: config.title || 'ACCTOO Export',
            filename: config.filename || `${config.title || 'export'}-excel`
          });
          break;

        case 'csv':
          result = exportEngine.exportToCSV(data, {
            ...config,
            title: config.title || 'ACCTOO Export',
            filename: config.filename || `${config.title || 'export'}-csv`
          });
          break;

        case 'json':
          result = exportEngine.exportToJSON(data, {
            ...config,
            title: config.title || 'ACCTOO Export',
            filename: config.filename || `${config.title || 'export'}-json`
          });
          break;

        case 'image':
          if (!config.elementId) {
            throw new Error('Element ID required for image export');
          }
          result = await exportEngine.exportToImage(config.elementId, {
            ...config,
            title: config.title || 'ACCTOO Export',
            filename: config.filename || `${config.title || 'export'}-image`
          });
          break;

        default:
          throw new Error(`Unsupported export format: ${format}`);
      }

      toast.success(`${format.toUpperCase()} export completed: ${result.filename}`);

      if (onExportComplete) onExportComplete(format, result);

    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Export failed: ${error.message}`);

      if (onExportError) onExportError(format, error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleBulkExport = async () => {
    if (!data || (Array.isArray(data) && data.length === 0)) {
      toast.error('No data available for export');
      return;
    }

    setIsExporting(true);
    setShowOptions(false);

    const formats = ['pdf', 'excel', 'csv'];
    const results = [];

    try {
      if (onExportStart) onExportStart('bulk');

      const exportEngine = new ExportEngine({
        company: companyProfile,
        currency: currency
      });

      for (const format of formats) {
        try {
          let result;
          switch (format) {
            case 'pdf':
              result = await exportEngine.exportToPDF(data, {
                ...config,
                title: config.title || 'ACCTOO Export',
                filename: config.filename || `${config.title || 'export'}-pdf`
              });
              break;
            case 'excel':
              result = await exportEngine.exportToExcel(data, {
                ...config,
                title: config.title || 'ACCTOO Export',
                filename: config.filename || `${config.title || 'export'}-excel`
              });
              break;
            case 'csv':
              result = exportEngine.exportToCSV(data, {
                ...config,
                title: config.title || 'ACCTOO Export',
                filename: config.filename || `${config.title || 'export'}-csv`
              });
              break;
          }
          results.push({ format, result });
        } catch (error) {
          console.error(`Bulk export error for ${format}:`, error);
        }
      }

      toast.success(`Bulk export completed: ${results.length} formats exported`);

      if (onExportComplete) onExportComplete('bulk', results);

    } catch (error) {
      console.error('Bulk export error:', error);
      toast.error(`Bulk export failed: ${error.message}`);

      if (onExportError) onExportError('bulk', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="relative inline-block">
      {/* Main Export Button */}
      <button
        onClick={() => setShowOptions(!showOptions)}
        disabled={disabled || isExporting}
        className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${className} ${
          disabled || isExporting ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        {isExporting ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Exporting...
          </>
        ) : (
          <>
            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export
          </>
        )}
      </button>

      {/* Export Options Dropdown */}
      {showOptions && (
        <div className="absolute right-0 z-10 mt-2 w-64 bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="py-1">
            <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-200">
              Export Options
            </div>

            {exportOptions.map((option) => (
              <button
                key={option.key}
                onClick={() => handleExport(option.key)}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
              >
                <div className="flex items-center">
                  <span className="text-lg mr-3">{option.icon}</span>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{option.label}</div>
                    <div className="text-xs text-gray-500">{option.description}</div>
                  </div>
                </div>
              </button>
            ))}

            <div className="border-t border-gray-200">
              <button
                onClick={handleBulkExport}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
              >
                <div className="flex items-center">
                  <span className="text-lg mr-3">📦</span>
                  <div>
                    <div className="text-sm font-medium text-gray-900">Export All</div>
                    <div className="text-xs text-gray-500">PDF, Excel & CSV</div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {showOptions && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowOptions(false)}
        />
      )}
    </div>
  );
};

export default GlobalExportButton;
